<script setup lang="ts">
import { useUserStore } from '@/stores/user';
import { useEmployeeStore } from '@/stores/employees';
import { Employee } from '@/lib/request/types/employee';
import ErrorMessageGeneric from '@/components/ErrorMessageGeneric.vue';
import PinDialog from '@/components/PinDialog.vue';
import ResetPinMethod from '@/components/ResetPinMethod.vue';
import ResetPinResponse from '../components/ResetPinResponse.vue';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import { getTranslations } from '@/lib/util/i18n';
import { onMounted } from 'vue';
import router from '@/router';

const t = getTranslations({
  app: '',
  module: 'views',
  file: 'reset-pin',
});

const props = defineProps<{
  employeeId: number;
}>();

const employee = useEmployeeStore().getByEmployeeId(
  props.employeeId
) as Employee;

const user = useUserStore();
const { resetPin, restartResetPin } = user;

onMounted(() => {
  restartResetPin();
});

function goToLogin() {
  router.push({ name: 'login', params: { id: props.employeeId } });
}

function clearError() {
  user.errors.resetPinError = null;
}
</script>

<template>
  <PinDialog
    @closed="$router.push({ name: 'home ' })"
    :show-close="Boolean(user.errors.resetPinError)"
  >
    <ResetPinResponse
      v-if="user.data.resetPinResponseMessage"
      :message="user.data.resetPinResponseMessage"
      @login="goToLogin"
    />
    <ErrorMessageGeneric
      v-else-if="user.errors.resetPinError"
      :title="t('error-title')"
      :message="user.errors.resetPinError.message"
      :action="t('try-again')"
      @clicked="clearError"
      data-test-id="error"
    />

    <LoadingSpinner v-else-if="user.status.resetPinLoading" />

    <ResetPinMethod
      v-else
      @back="goToLogin"
      @select="
        (mode) => {
          resetPin(props.employeeId, mode);
        }
      "
      :email="employee.EmailAddress"
      :mobile="employee.Mobile"
    />
  </PinDialog>
</template>

<style scoped>
/* centers spinner */
.loading-spinner {
  display: flex;
}
</style>
